import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import brandLogo from '../assets/childhood-horizons-logo.jpeg';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'الرئيسية', path: '/' },
    { name: 'من نحن', path: '/about' },
    { name: 'الخدمات الاستشارية', path: '/#services' },
    { name: 'مركز المعرفة', path: '/knowledge' },
    { name: 'البرامج المهنية', path: '/programs' },
    { name: 'بنك الكفاءات', path: '/talent-bank' },
    { name: 'تواصل معنا', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navbar"
      className="fixed top-0 right-0 left-0 z-50 backdrop-blur-xl bg-[#FAFAF7]/85 border-b border-[#D5D1C8]/60"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[110px] md:h-[124px]">
          {/* Full brand logo — used as-is, cropped only to hide the 4 icons row */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center group"
          >
            <div
              className="relative h-[80px] sm:h-[92px] md:h-[104px] w-[98px] sm:w-[112px] md:w-[128px] shrink-0 transition-transform duration-300 group-hover:scale-[1.02]"
              aria-hidden="true"
            >
              <img
                src={brandLogo}
                alt="Childhood Horizons — آفاق الطفولة"
                className="w-full h-full object-contain select-none pointer-events-none"
                style={{ mixBlendMode: 'multiply' }}
                draggable="false"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-7 lg:gap-9">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-[14px] lg:text-[15px] font-medium transition-all duration-300 relative group tracking-tight ${
                  isActive(link.path)
                    ? 'text-[#1F3D2B]'
                    : 'text-[#5C6D49] hover:text-[#1F3D2B]'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1.5 right-0 h-[2px] bg-[#4A5D23] transition-all duration-300 ${
                  isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
            <Link to="/contact" className="ms-2">
              <button 
                data-testid="nav-cta-btn"
                className="bg-[#1F3D2B] hover:bg-[#163020] text-[#FAFAF7] px-5 py-2.5 rounded-xl text-[13px] lg:text-sm font-semibold tracking-tight transition-all duration-300 hover:-translate-y-0.5 shadow-[0_4px_12px_rgba(31,61,43,0.25)]"
              >
                تواصل معنا
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-[#2C3814] hover:bg-[#F3EFE9] rounded-xl transition-colors"
          >
            {isOpen ? <X size={28} weight="bold" /> : <List size={28} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#FAFAF7] border-t border-[#D5D1C8]"
          >
            <div className="px-6 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  onClick={() => setIsOpen(false)}
                  className={`block py-3 px-4 text-lg font-medium rounded-xl transition-all ${
                    isActive(link.path)
                      ? 'text-[#2C3814] bg-[#F3EFE9]'
                      : 'text-[#7A8A62] hover:bg-[#F3EFE9]'
                  }`}
                >
                  <span className="block">{link.name}</span>
                </Link>
              ))}
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <button className="w-full bg-[#2C3814] text-[#FAFAF7] py-3 rounded-xl font-medium mt-2">
                  تواصل معنا
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
